import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/axios'

// IPSC (migration 016): escolha da divisao e do Power Factor na inscricao (publica e do painel).

export interface DivisaoIpsc {
  iddivisao: number
  nome: string
  sigla: string | null
  ordem: number
  power_factor_permitido: 'ambos' | 'major' | 'minor'
  ativo: boolean
}

export interface EscolhaIpsc {
  iddivisao: number | ''
  power_factor: '' | 'major' | 'minor'
}

export const escolhaVazia: EscolhaIpsc = { iddivisao: '', power_factor: '' }

// lista das divisoes ativas (rota publica: serve para a inscricao publica e o painel)
export function useDivisoesIpsc(ativo = true) {
  return useQuery({
    queryKey: ['ipsc-divisoes-publicas'],
    queryFn: () => api.get<{ data: DivisaoIpsc[] }>('/publico/ipsc/divisoes').then((r) => r.data.data),
    enabled: ativo,
    staleTime: 5 * 60 * 1000,
  })
}

export function pfPermitidos(d: DivisaoIpsc | undefined): Array<'major' | 'minor'> {
  if (!d) return ['major', 'minor']
  return d.power_factor_permitido === 'ambos' ? ['major', 'minor'] : [d.power_factor_permitido]
}

// escolheu a divisao: se ela so aceita um PF, ja escolhe; se o PF atual nao vale, limpa
export function mudarDivisao(divisoes: DivisaoIpsc[], atual: EscolhaIpsc, iddivisao: number | ''): EscolhaIpsc {
  const d = divisoes.find((x) => x.iddivisao === iddivisao)
  const pfs = pfPermitidos(d)
  const pf = pfs.length === 1 ? pfs[0] : pfs.includes(atual.power_factor as 'major' | 'minor') ? atual.power_factor : ''
  return { iddivisao, power_factor: pf }
}

export const escolhaCompleta = (e: EscolhaIpsc | undefined) => !!e && e.iddivisao !== '' && e.power_factor !== ''

export const nomePf = (pf: string | null | undefined) => (pf === 'major' ? 'Major' : pf === 'minor' ? 'Minor' : '')

export function EscolhaDivisaoIpsc({
  divisoes, valor, onChange, tema = 'painel', idPrefixo,
}: {
  divisoes: DivisaoIpsc[]
  valor: EscolhaIpsc
  onChange: (v: EscolhaIpsc) => void
  tema?: 'painel' | 'publico'
  idPrefixo: string
}) {
  const d = divisoes.find((x) => x.iddivisao === valor.iddivisao)
  const pfs = pfPermitidos(d)
  const cls = tema === 'publico'
    ? 'w-full rounded-lg bg-black/40 border border-white/15 text-white text-sm px-2 py-2 focus:outline-none focus:border-[#caa14d]'
    : 'w-full px-3 py-2 border border-compumaq-dourado/25 bg-compumaq-preto text-compumaq-texto rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-compumaq-dourado'
  const rotulo = tema === 'publico' ? 'block text-[11px] text-white/50 mb-1' : 'block text-xs font-medium text-compumaq-textoSuave mb-1'
  return (
    <div className="grid grid-cols-2 gap-2 mt-2" data-ipsc-escolha={idPrefixo}>
      <div>
        <label className={rotulo} htmlFor={`${idPrefixo}-divisao`}>Divisão IPSC *</label>
        <select id={`${idPrefixo}-divisao`} className={cls} value={valor.iddivisao} aria-label="Divisão IPSC"
          onChange={(e) => onChange(mudarDivisao(divisoes, valor, e.target.value ? Number(e.target.value) : ''))}>
          <option value="">Escolha a divisão</option>
          {divisoes.map((x) => (
            <option key={x.iddivisao} value={x.iddivisao}>{x.nome}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={rotulo} htmlFor={`${idPrefixo}-pf`}>Power Factor *</label>
        <select id={`${idPrefixo}-pf`} className={cls} value={valor.power_factor} aria-label="Power Factor"
          onChange={(e) => onChange({ ...valor, power_factor: e.target.value as EscolhaIpsc['power_factor'] })}>
          <option value="">Escolha</option>
          {pfs.map((pf) => (
            <option key={pf} value={pf}>{nomePf(pf)}</option>
          ))}
        </select>
      </div>
    </div>
  )
}
